import { useEffect } from "react";

import { useFeatureState } from "./FeaturesProvider";
import { fetchEvaluateFeature, parsePartition } from "./utils";

const noop = () => {};

type NormalizeArgs<T> = T extends any[] ? T : [T];

export type FeatureHook<TArgs, TReturn> = (
  ...args: NormalizeArgs<TArgs>
) => TReturn;

export type FeatureHookVariations<TArgs, TReturn> = (FeatureHook<
  TArgs,
  TReturn
> | null)[];

interface CreateFeatureHookArgs<TArgs, TReturn> {
  name: string;
  variations: (FeatureHook<TArgs, TReturn> | null)[];
  version?: 1 | 2;
}

function getVariationHook<TArgs, TReturn>({
  variations,
  index,
  version,
}: {
  variations: (FeatureHook<TArgs, TReturn> | null)[];
  index: number | undefined;
  version: number;
}): FeatureHook<TArgs, TReturn> | null {
  const parsedPartition = parsePartition({ index, variations, version });
  return variations[parsedPartition];
}

export function createFeatureHook<TArgs, TReturn>({
  name,
  variations,
  version,
}: CreateFeatureHookArgs<TArgs, TReturn>): FeatureHook<TArgs, TReturn> {
  return ((...args: any[]) => {
    const { status, index, evaluable, context, evaluatedFeatures } =
      useFeatureState(name);

    useEffect(() => {
      if (
        status === "success" &&
        version !== 1 &&
        !evaluatedFeatures.current.has(name)
      ) {
        evaluatedFeatures.current.add(name);
        fetchEvaluateFeature({ context, evaluable: !!evaluable, index, name });
      }
    }, [status, evaluable, index, context, evaluatedFeatures]);

    let wantedVariation = Array.isArray(variations) && variations[0];

    if (status === "success") {
      wantedVariation = getVariationHook<TArgs, TReturn>({
        index,
        variations,
        version: version ?? 2,
      })!;
    }

    return wantedVariation
      ? wantedVariation(...(args as NormalizeArgs<TArgs>))
      : noop();
  }) as FeatureHook<TArgs, TReturn>;
}
